import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpenCheck, ClipboardList, Microscope, GitBranch } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * /methodology — Explains how BlendSafe scores safety. Designed as an
 * SEO-friendly trust page that satisfies both first-time visitors and
 * skeptical dermatologists. No images — just calm, well-spaced copy.
 */
const STEPS = [
  {
    icon: BookOpenCheck,
    title: "Where we start: the medication label",
    body: (
      <>
        Every combination begins with the medication&rsquo;s own FDA label on{" "}
        <a
          href="https://dailymed.nlm.nih.gov/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-ink underline decoration-peach-400 underline-offset-2 hover:decoration-peach-500"
        >
          DailyMed
        </a>
        {" "}&mdash; the official prescribing information for that exact product. We
        read what the label does, and doesn&rsquo;t, say about using it alongside a
        skincare ingredient.
      </>
    ),
  },
  {
    icon: Microscope,
    title: "When the label doesn't cover it",
    body: (
      <>
        If the label is silent on an ingredient, our research may draw on a set
        list of trusted clinical sources (listed below). A concern described in a
        medication&rsquo;s own label is not the same as one supported by other
        clinical guidance or research &mdash; and a label saying nothing about an
        ingredient is not proof the combination is safe.
      </>
    ),
  },
  {
    icon: ClipboardList,
    title: "Clinical review",
    body: (
      <>
        Research for each combination is prepared by a PharmD student, then
        reviewed and signed off by a licensed US pharmacist before a
        medication&rsquo;s results are published. Nothing goes live without that
        review.
      </>
    ),
  },
  {
    icon: GitBranch,
    title: "Grading, and keeping it current",
    body: (
      <>
        Each reviewed interaction is graded by how serious it is &mdash; avoid, use
        with caution, or no label conflict found &mdash; based on the cited source,
        and recorded with who reviewed it and the date, so it can be audited. If a
        label is updated, a study is retracted, or a reviewer flags a result, the
        change goes through the same review before anything visible changes &mdash;
        no silent edits.
      </>
    ),
  },
];

const SOURCES = [
  { label: "DailyMed (FDA drug labels)", href: "https://dailymed.nlm.nih.gov/" },
  { label: "FDA.gov", href: "https://www.fda.gov/" },
  { label: "PubMed", href: "https://pubmed.ncbi.nlm.nih.gov/" },
  { label: "American Academy of Dermatology (AAD)", href: "https://www.aad.org/" },
  { label: "Journal of the AAD (JAAD)", href: "https://www.jaad.org/" },
  { label: "DermNet NZ", href: "https://dermnetnz.org/" },
  { label: "Cochrane Library", href: "https://www.cochranelibrary.com/" },
  { label: "Drugs.com", href: "https://www.drugs.com/" },
  { label: "Medscape", href: "https://www.medscape.com/" },
];

export default function Methodology() {
  return (
    <main
      data-testid="methodology-page"
      className="bg-cream-50 min-h-[80vh]"
    >
      <SeoHead
        title="BlendSafe Methodology — How we score skincare safety"
        description="How BlendSafe rates the interaction between every skincare ingredient and every supported medication: sources, clinician review, severity tiers, and audit trail."
        canonicalPath="/methodology"
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
          The BlendSafe methodology
        </p>
        <h1
          data-testid="methodology-headline"
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
        >
          How we check a combination against the label.
        </h1>
        <p className="text-[14.5px] sm:text-[15.5px] text-ink-soft/85 mt-5 leading-relaxed max-w-xl">
          BlendSafe is an informational tool, not a diagnostic device. Here's
          the exact pipeline behind every verdict — sources, review process,
          severity tiers, and what we do when the literature disagrees.
        </p>

        <section data-testid="methodology-steps" className="mt-12 space-y-4">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <article
              key={title}
              data-testid={`methodology-step-${i + 1}`}
              className="rounded-2xl bg-white border border-surface-line p-5 sm:p-6"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cream-100 border border-surface-line shrink-0">
                  <Icon className="w-4 h-4 text-ink-soft" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                    Step {i + 1}
                  </div>
                  <h2 className="mt-1 text-[16.5px] sm:text-[17.5px] font-semibold text-ink">
                    {title}
                  </h2>
                  <p className="mt-2 text-[13.5px] sm:text-[14px] text-ink-soft leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </section>

        <section
          data-testid="methodology-sources"
          className="mt-10 rounded-2xl bg-white border border-surface-line p-5 sm:p-6"
        >
          <h2 className="text-[16.5px] sm:text-[17.5px] font-semibold text-ink">
            Sources we rely on
          </h2>
          <p className="mt-2 text-[13.5px] sm:text-[14px] text-ink-soft leading-relaxed">
            We always start with the medication label, and only draw on this set
            list of clinical sources when the label doesn&rsquo;t address an
            ingredient.
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {SOURCES.map((s, i) => (
              <li key={s.label}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`methodology-source-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-surface-line bg-cream-50 px-3 py-1.5 text-[12.5px] font-medium text-ink-soft hover:text-ink hover:border-ink/30 transition-colors"
                >
                  {s.label}
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section
          data-testid="methodology-disclaimer"
          className="mt-12 rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6"
        >
          <h2 className="text-[15px] font-semibold text-amber-900">
            Important — not medical advice
          </h2>
          <p className="mt-2 text-[13.5px] text-amber-900/85 leading-relaxed">
            BlendSafe is an informational tool. It does not replace a
            consultation with your dermatologist, OB-GYN, or pharmacist. If a
            verdict suggests AVOID for a product you depend on, talk to a
            clinician before making any change to your medication or routine.
          </p>
        </section>

        <div className="mt-12 flex flex-wrap gap-2">
          <Link
            to="/check"
            data-testid="methodology-cta-check"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
          >
            Try the checker
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/faq"
            data-testid="methodology-cta-faq"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
          >
            Read the FAQ
          </Link>
        </div>
      </div>
    </main>
  );
}
